import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import api from '@/services/auth/api';
import { decodeToken } from '@/utils/auth';
import { useAtomicSetter } from '@/composables/useAtomicSetter.js';
import { SionUserTypes } from '@/constants/sion.js';

export const useAuthStore = defineStore('auth', () => {
  const token = ref(null);
  const decodedToken = ref(null);

  const stateRefs = {
    token,
    decodedToken,
  };

  const suppliers = computed(() => {
    return decodedToken.value?.['https://www.sioncentral.com/suppliers'] || [];
  });

  const supplierId = computed(() => {
    return decodedToken.value?.['https://www.sioncentral.com/suppliers']?.at(0) || null;
  });

  const agencyId = computed(() => {
    return decodedToken.value?.['https://www.sioncentral.com/agency'] || null;
  });

  const userType = computed(() => {
    if (suppliers.value.length) return SionUserTypes.SUPPLIER;
    if (agencyId.value) return SionUserTypes.AGENCY;
    return SionUserTypes.SUPPLIER;
  });

  const isAgency = computed(() => {
    return userType.value === SionUserTypes.AGENCY;
  });

  const permissions = computed(() => {
    return decodedToken.value?.permissions || [];
  });

  const { setAtomic } = useAtomicSetter(stateRefs);

  const getAuthenticated = async () => {
    await api.getAuthenticated();
  };

  const getHeartbeatAuthorized = async () => {
    await api.getHeartbeatAuthorized();
  };

  const hasPermission = (permission) => {
    if (!permission) return false;
    return permissions.value.includes(permission);
  };

  const hasAnyPermission = (requiredPermissions = []) => {
    if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) return false;
    return requiredPermissions.some((permission) => permissions.value.includes(permission));
  };

  const hasAllPermissions = (requiredPermissions = []) => {
    if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) return false;
    return requiredPermissions.every((permission) => permissions.value.includes(permission));
  };

  const setToken = (authToken) => {
    setAtomic('token', authToken);
    setAtomic('decodedToken', decodeToken(authToken));
  };

  return {
    ...stateRefs,
    setToken,
    getAuthenticated,
    getHeartbeatAuthorized,
    setAtomic,
    agencyId,
    supplierId,
    suppliers,
    permissions,
    userType,
    isAgency,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
});
